// Shared input "shell" — traditional form field with a label stacked
// above a bordered text box. Used across VerifyAddressForm, the /submit
// email field, /cancel, /refund, etc. Label sits above so it reads as a
// form to fill out; the input below carries its own visible border and
// padding so it's obviously an editable text box.
//
// Variants via transient props:
//   $disabled     — muted surface + not-allowed cursor
//   $hasError     — red border on the input + blush fill
//   $readonlyHelp — sunken input + help cursor (intentionally
//                   non-editable, paired with a tooltip)
//   $stacked      — bottom margin so consecutive shells get air
//                   between them

import styled, { css } from "styled-components";
import { theme } from "../../constants/theme";

export const FieldShell = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  margin-bottom: ${({ $stacked }) => ($stacked ? "1rem" : "0")};

  /* Bootstrap input-group: stack prepend label above input, override its
     default row flex behavior. */
  .input-group {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    flex-wrap: nowrap;
    width: 100%;
  }

  /* Label */
  .input-group-prepend {
    margin: 0 0 0.35rem 0;
    display: block;
  }

  .input-group-text {
    background: transparent;
    border: none;
    padding: 0;
    color: ${theme.color.slate};
    font-size: ${theme.type.sm};
    font-weight: ${theme.weight.semibold};
    letter-spacing: 0.01em;
    user-select: none;
    display: inline-block;
  }

  .input-group-text i {
    font-size: 0.95rem;
    color: ${theme.color.textSubtle};
    margin-right: 0.4rem;
  }

  /* Editable text box — the visible, clearly-editable frame. */
  .form-control {
    background: ${theme.color.surface} !important;
    border: 1.5px solid ${theme.color.borderStrong} !important;
    border-radius: ${theme.radius.md} !important;
    padding: 0.7rem 0.9rem !important;
    font-size: ${theme.type.base} !important;
    font-weight: ${theme.weight.medium} !important;
    color: ${theme.color.text} !important;
    box-shadow: ${theme.shadow.sm} !important;
    outline: none !important;
    cursor: text;
    width: 100%;
    min-width: 0;
    transition:
      border-color ${theme.motion.base},
      box-shadow ${theme.motion.base};
  }

  .form-control:hover {
    border-color: ${theme.color.slateSoft} !important;
  }

  .form-control:focus {
    border-color: ${theme.color.salmon} !important;
    box-shadow: ${theme.shadow.sm}, ${theme.shadow.ring} !important;
  }

  .form-control::placeholder {
    color: ${theme.color.textSubtle} !important;
    font-weight: ${theme.weight.regular} !important;
    opacity: 1;
  }

  /* Hide number-input spinners — don't match the aesthetic. */
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance: textfield;
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      .form-control {
        background: ${theme.color.surfaceSunk} !important;
        border-color: ${theme.color.border} !important;
        box-shadow: none !important;
        color: ${theme.color.textMuted} !important;
        cursor: not-allowed;
      }
      .form-control:hover {
        border-color: ${theme.color.border} !important;
      }
      .input-group-text {
        color: ${theme.color.textMuted};
      }
    `}

  ${({ $hasError }) =>
    $hasError &&
    css`
      .form-control {
        border-color: ${theme.color.error} !important;
        background: #fff6f5 !important;
      }
      .form-control:hover {
        border-color: ${theme.color.error} !important;
      }
      .form-control:focus {
        border-color: ${theme.color.error} !important;
        box-shadow:
          ${theme.shadow.sm},
          0 0 0 3px rgba(220, 53, 69, 0.22) !important;
      }
    `}

  ${({ $readonlyHelp }) =>
    $readonlyHelp &&
    css`
      .form-control {
        background: ${theme.color.surfaceSunk} !important;
        color: ${theme.color.textMuted} !important;
        cursor: help;
      }
    `}
`;

export const FieldError = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  color: ${theme.color.error};
  background: #fff0ef;
  border-left: 3px solid ${theme.color.error};
  padding: 0.45rem 0.65rem;
  border-radius: ${theme.radius.sm};
  font-size: ${theme.type.sm};
  font-weight: ${theme.weight.medium};
  margin: 0.4rem 0 0.75rem 0;
  line-height: 1.35;

  &::before {
    content: "!";
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.1rem;
    height: 1.1rem;
    border-radius: 999px;
    background: ${theme.color.error};
    color: #fff;
    font-size: 0.75rem;
    font-weight: ${theme.weight.bold};
    flex: 0 0 auto;
    line-height: 1;
    margin-top: 0.05rem;
  }
`;
